import React, { useState, useEffect, useRef } from "react";
import { startCase } from "lodash";
import pluralize from "pluralize";
import generateReceiptPdf from "./shared/generateReceiptPdf";
import SummaryCard from "./shared/SummaryCard";
import { Badge } from "../common/Badge";
import EntitySummary from "./EntitySummary";
import SubmissionReceiptSkeleton from "./shared/SubmissionReceiptSkeleton";
import PartyPopper from "./shared/PartyPopper";
import { CheckRounded } from "@material-ui/icons";
import { FileDownloadOutlined, ErrorOutlineOutlined } from "@mui/icons-material";
import logo from "~/images/logo.png";
import { wholeNumberWithCommas } from "../utils/NumberFormatter";

const POLL_INTERVAL = 5000;
// Tolerates a transient failure (e.g. hitting the summary endpoint mid-write on the backend)
// instead of stranding the user on an error after a single bad poll.
const MAX_CONSECUTIVE_FAILURES = 3;

const SubmissionSuccess = (props) => {
  const [processing, setProcessing] = useState(true);
  const [submissionReceipt, setSubmissionReceipt] = useState(null);
  const [noClaimAccess, setNoClaimAccess] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);

  useEffect(() => {
    if (!props.summaryPath) return;

    const poll = () => {
      fetch(props.summaryPath, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch summary");
          return res.json();
        })
        .then((data) => {
          consecutiveFailuresRef.current = 0;
          if (data.status === "complete") {
            setSubmissionReceipt(data);
            setNoClaimAccess(!data.summary.claim_access_counts.accessible);
            setProcessing(false);
            clearInterval(intervalRef.current);
          }
        })
        .catch((err) => {
          consecutiveFailuresRef.current += 1;
          if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
            setError(err.message);
            clearInterval(intervalRef.current);
          }
        });
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL);

    return () => clearInterval(intervalRef.current);
  }, [props.summaryPath]);

  const renderHeader = () => {
    return (
      <div className="success__header">
        <div className="header__logo"></div>
        <a href={props.donePath}>
          <button className="button button--lg button--blue">Return to Submissions</button>
        </a>
      </div>
    );
  };

  const renderSuccessMessage = () => {
    return (
      <div className="success__content animated fadeInDown fadeInDown--small">
        <div className="success__content__icon success__content__icon--check">
          <CheckRounded />
        </div>
        <div className="success__title">Success!</div>
        <div className="success__subtitle">Your claims submission has been processed.</div>
        {!noClaimAccess && (
          <div className="success__notice success__highlight-text">
            Use the Submission Receipt to review your submission results and validation summary. You
            can download the submitted claims and receipt for your records, or return to these
            details later in the applicable Claims Data module.
          </div>
        )}
        {!noClaimAccess && (
          <div className="success__downloads">
            <div className="flex gap--16">
              <a
                href={props.downloadPath}
                className="button button--white button--lg"
                aria-label="Download Submission Claims"
              >
                <FileDownloadOutlined style={{ fontSize: "18px" }} /> Download Claims (.csv)
              </a>
              <button
                className="button button--white button--lg"
                onClick={() => generateReceiptPdf(submissionReceipt)}
                aria-label="Download Submission Receipt"
              >
                <FileDownloadOutlined style={{ fontSize: "18px" }} /> Download Receipt (.pdf)
              </button>
            </div>
          </div>
        )}
        {noClaimAccess && render340bIdAccessBanner("spaced")}
      </div>
    );
  };

  const render340bIdAccessBanner = (marginType = "offset") => {
    const inaccessibleRowCount = submissionReceipt.summary.claim_access_counts.inaccessible;

    return (
      inaccessibleRowCount > 0 && (
        <div className={`banner__warning banner__warning--${marginType}`}>
          <span className="material-symbols-rounded banner__warning__icon">warning</span>
          <div className="banner__warning__text">
            This submission contained {inaccessibleRowCount}{" "}
            {pluralize("row", inaccessibleRowCount)} related to invalid 340B IDs and/or 340B IDs
            that you do not have access to view. You will not be able to view data related to these
            340B IDs. If you think you should have access to these 340B IDs, please contact your
            administrator.
          </div>
        </div>
      )
    );
  };

  const renderReceiptHeader = () => {
    return (
      <div className="submission_preview__header">
        <div className="submission_preview__header__details">
          <div className="submission_preview__subtitle">Submission Receipt</div>
          <div className="submission_preview__title submission_preview__title--lg submission_preview__title--bold">
            {submissionReceipt.name}
          </div>
          <div className="submission_preview__title">
            Date Submitted: {submissionReceipt.submission_date}
          </div>
        </div>
        <div className="submission_preview__header__user">
          <div className="submission_preview__user__avatar">
            <div className="submission_preview__user__avatar__initials">
              {submissionReceipt.submitted_by.initials}
            </div>
          </div>
          <div className="submission_preview__user__text">
            <div className="submission_preview__user__text__title">
              {submissionReceipt.submitted_by.name}
            </div>
            <div className="submission_preview__user__text__subtitle">
              {submissionReceipt.submitted_by.email}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderValidationMetrics = () => {
    return (
      <div className="submission_preview__card">
        <div className="submission_preview__card__content">
          <div className="submission_preview__subtitle">Metrics</div>
        </div>
        <div className="submission_preview__divider submission_preview__divider--no-mg"></div>
        <div className="submission_preview__card__content">
          <div className="flex flex-column gap--16">
            {submissionReceipt.summary.metrics.map((metric) => {
              let fillModifier;
              switch (metric.title) {
                case "Conforming": {
                  fillModifier = "green";
                  break;
                }
                case "Non-Conforming": {
                  fillModifier = "red";
                  break;
                }
                case "Claims with Warnings": {
                  fillModifier = "orange";
                  break;
                }
              }
              let barClass = ` progress_bar__fill--${fillModifier}`;
              let textClass = ` t--${fillModifier}`;

              return (
                <div key={metric.title} className="flex flex-column">
                  <div className="submission_preview__title submission_preview__title--bold">
                    {metric.title}
                  </div>
                  <div className="submission_preview__description">{metric.description}</div>
                  <div className="flex flex-column gap--8">
                    <div className="progress_bar__container">
                      <div
                        className={`progress_bar__fill${barClass}`}
                        style={{ width: `${metric.percentage}%` }}
                      />
                    </div>
                    <div className="flex justify-space-between">
                      <div className="submission_preview__stat_value--sm t--subtitle">
                        {wholeNumberWithCommas(metric.count)}
                      </div>
                      <div className={`submission_preview__stat_value--sm${textClass}`}>
                        {metric.percentage}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderEntitiesAndManufacturers = () => {
    return (
      <div className="submission_preview__card">
        <div className="submission_preview__card__content">
          <div className="submission_preview__subtitle">Covered Entities & Manufacturers</div>
        </div>
        <div className="submission_preview__divider submission_preview__divider--no-mg"></div>
        <div className="submission_preview__card__content flex-fill">
          <div className="flex justify-space-between align-center">
            <div className="submission_preview__title submission_preview__title--bold">
              Covered Entities
            </div>
            <div className="submission_preview__stat_value--sm">
              {wholeNumberWithCommas(submissionReceipt.summary.entities.length)}
            </div>
          </div>
          <div className="flex flex-column gap--4 mt--8 submission_preview__scroll-list">
            {submissionReceipt.summary.entities.map((ce) => {
              return (
                <div key={ce.id_340b} className="flex justify-space-between align-center">
                  <span>{ce.name}</span>
                  <span>{ce.id_340b}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="submission_preview__divider submission_preview__divider--no-mg"></div>
        <div className="submission_preview__card__content flex-fill">
          <div className="flex justify-space-between align-center">
            <div className="submission_preview__title submission_preview__title--bold">
              Manufacturers
            </div>
            <div className="submission_preview__stat_value--sm">
              {wholeNumberWithCommas(submissionReceipt.summary.manufacturers.length)}
            </div>
          </div>
          <div className="mt--8 submission_preview__scroll-list submission_preview__two-col">
            {submissionReceipt.summary.manufacturers.map((mfr) => {
              return (
                <div key={mfr} className="flex justify-space-between align-center">
                  <span>{startCase(mfr)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderIssuesWarningsCard = (data) => {
    return (
      <div className="submission_preview__card">
        <div className="submission_preview__card__content submission_preview__card__content--no-btm flex-grow-0">
          <div className="submission_preview__title-container">
            <div className="submission_preview__title submission_preview__title--upcase">
              {data.title}
            </div>
            <div className="submission_preview__description">{data.description}</div>
          </div>
          <div className="submission_preview__stat_value">{wholeNumberWithCommas(data.count)}</div>
        </div>
        <div className="submission_preview__scroll-table flex-fill mt--16">
          <table className="summary_table summary_table--square summary_table--no-border summary_table--lg-header">
            <thead>
              <tr>
                <th>{data.type} Type</th>
                <th>Count</th>
                <th>
                  % of <br />
                  Total Claims
                </th>
              </tr>
            </thead>
            <tbody className="sm">
              {Object.values(data.data).map((item) => (
                <tr key={item.label}>
                  <td>
                    <Badge
                      color={data.type === "Issue" ? "red" : "orange"}
                      className="status_tag__container--fixed"
                      wrapText
                    >
                      {item.label}
                    </Badge>
                  </td>
                  <td>{wholeNumberWithCommas(item.count)}</td>
                  <td>{item.percentage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderEntityClaimsTable = () => {
    return (
      <>
        {submissionReceipt.entities.map((ce) => (
          <EntitySummary
            key={ce.id_340b}
            title={<div className="entity_summary__title">{ce.id_340b}</div>}
            subtitle={<div className="entity_summary__subtitle">{ce.name}</div>}
            stats={ce.summary_counts}
            rowCount={ce.total_rows}
            conformingPercentage={ce.conforming_percentage}
            table={
              <table className="summary_table">
                <thead>
                  <tr>
                    <th>Manufacturer</th>
                    <th>Product</th>
                    <th>NDC</th>
                    <th>Conforming</th>
                    <th>Non-Conforming</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(ce.ndc_stats).map((ndcDetails) => (
                    <tr key={ndcDetails.ndc}>
                      <td>{ndcDetails.manufacturer}</td>
                      <td>{ndcDetails.product_name}</td>
                      <td>{ndcDetails.ndc}</td>
                      <td>{wholeNumberWithCommas(ndcDetails.conforming_count)}</td>
                      <td>{wholeNumberWithCommas(ndcDetails.non_conforming_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
            numberColumns={5}
          />
        ))}
      </>
    );
  };

  if (error) {
    return (
      <div className="success__wrapper">
        {renderHeader()}
        <div className="success__content">
          <div className="success__content__icon success__content__icon--error">
            <ErrorOutlineOutlined />
          </div>
          <div className="success__title">Something went wrong</div>
          <div className="success__subtitle">{error}</div>
        </div>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="success__wrapper">
        {renderHeader()}
        <div className="success__content">
          <div className="success__content__icon success__content__icon--party">
            <PartyPopper />
          </div>
          <div className="success__title">You're all set!</div>
          <div className="success__subtitle success__subtitle--processing">
            Your claims submission has been received.
          </div>
          <div className="success__subtitle success__subtitle--processing-detail success__highlight-text">
            340B ESP is now validating your claims, which may take several minutes. You can continue
            to use 340B ESP while validation is in progress.
          </div>
          <div className="success__cta">
            <a href={props.newSubmissionPath}>
              <button className="button button--lg button--blue">Make Another Submission</button>
            </a>
          </div>
        </div>
        <SubmissionReceiptSkeleton />
      </div>
    );
  }

  return (
    <div className="success__wrapper">
      {renderHeader()}
      {renderSuccessMessage()}
      {!noClaimAccess && (
        <div className="submission_preview submission_preview--receipt animated fadeInDown fadeInDown--small">
          {renderReceiptHeader()}
          <div className="submission_preview__divider"></div>
          {render340bIdAccessBanner()}
          <div className="submission_preview__content">
            {submissionReceipt.summary.counts.map((stat) => (
              <SummaryCard
                key={stat.title}
                title={stat.title}
                description={stat.description}
                value={stat.value}
              />
            ))}
          </div>
          <div className="submission_preview__divider">
            <div className="submission_preview__divider__title">Validation Summary</div>
          </div>
          <div className="submission_preview__content">
            {renderValidationMetrics()}
            {renderEntitiesAndManufacturers()}
          </div>
          <div className="submission_preview__divider">
            <div className="submission_preview__divider__title">Issues & Warnings</div>
          </div>
          <div className="submission_preview__content">
            {[submissionReceipt.issues, submissionReceipt.warnings].map((data) =>
              renderIssuesWarningsCard(data)
            )}
          </div>
          <div className="submission_preview__divider">
            <div className="submission_preview__divider__title">Claims By Covered Entities</div>
          </div>
          <div className="submission_preview__entities">{renderEntityClaimsTable()}</div>
        </div>
      )}
    </div>
  );
};

export default SubmissionSuccess;
